/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { AiOutlineFullscreen } from "react-icons/ai";
import { MdOutlineClose, MdPause, MdPlayArrow } from "react-icons/md";
import { useFontStyle } from "~/hook/useFontStyle";
import useStopCurrentRunningVideoAudio from "~/hook/useStopCurrentRunningVideoAudio";
import { onPauseVideo, videoDataType } from "~/lib/store/slice/youtubePlayerSlice";
import { useAppDispatch } from "~/lib/store/store";
import { cn } from "~/lib/utils";
import { isIOS } from "~/utils/helper-funcations";
import YoutubeVideoPlayer from "../shared-components/YoutubeVideoPlayer";

interface MobileVideoPopupProps {
  currentVideo: { videoId: string; from: string } | null;
  currentPlayVideo: videoDataType;
  playListQueueData: videoDataType[];
  isVideoPlay: boolean;
  isMute: boolean;
  onClose: () => void;
  onFullScreen: () => void;
  onNextVideo: () => void;
  onPreviousVideo: () => void;
  onPlayPause: (e?: React.MouseEvent | React.TouchEvent) => void;
  onHandleMuteUnMute: () => void;
}

function MobileVideoPopup({
  currentVideo,
  currentPlayVideo,
  playListQueueData,
  isVideoPlay,
  isMute,
  onClose,
  onFullScreen,
  onNextVideo,
  onPreviousVideo,
  onPlayPause,
  onHandleMuteUnMute,
}: MobileVideoPopupProps) {
  const { medium } = useFontStyle();
  const { playCurrentVideo } = useStopCurrentRunningVideoAudio();
  const dispatch = useAppDispatch();

  // Mobile drag functionality with momentum and pinch-to-zoom
  const [isDragging, setIsDragging] = useState(false);
  const [isPinching, setIsPinching] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [velocity, setVelocity] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [showControls, setShowControls] = useState(true);
  const [showCloseZone, setShowCloseZone] = useState(false);
  const [isOverCloseZone, setIsOverCloseZone] = useState(false);
  const controlsTimeoutRef = useRef<NodeJS.Timeout>();

  // Refs for advanced drag functionality
  const dragRef = useRef<HTMLDivElement>(null);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const initialMousePos = useRef({ x: 0, y: 0 });
  const lastMoveTime = useRef(Date.now());
  const lastPosition = useRef({ x: 0, y: 0 });
  const animationRef = useRef<number>();
  const initialDistance = useRef(0);
  const initialScale = useRef(1);

  // Initial position setup for mobile (60% from top)
  useEffect(() => {
    if (position.x === 0 && position.y === 0) {
      setPosition({
        x: window.innerWidth - 256, // 240px width + 16px margin
        y: window.innerHeight * 0.6, // 60% from top
      });
    }
  }, [position.x, position.y]);

  // Helper functions for smooth dragging
  const getDistance = (touch1: React.Touch, touch2: React.Touch) => {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const applyBoundaries = useCallback(
    (x: number, y: number) => {
      // Medium player size
      const playerWidth = 240 * scale;
      const playerHeight = 135 * scale;

      // Allow going out of screen only on bottom side
      const boundedX = Math.max(8, Math.min(window.innerWidth - playerWidth - 8, x));
      const boundedY = Math.max(8, y); // Remove bottom boundary - allow going out on bottom

      return { x: boundedX, y: boundedY };
    },
    [scale],
  );

  // Momentum animation with easing
  const animateWithMomentum = useCallback(() => {
    if (Math.abs(velocity.x) < 0.5 && Math.abs(velocity.y) < 0.5) {
      setVelocity({ x: 0, y: 0 });
      return;
    }

    const friction = 0.92; // Smooth deceleration like JioHotstar
    const newVelocity = {
      x: velocity.x * friction,
      y: velocity.y * friction,
    };

    const newPos = applyBoundaries(position.x + newVelocity.x, position.y + newVelocity.y);

    setPosition(newPos);
    setVelocity(newVelocity);

    animationRef.current = requestAnimationFrame(animateWithMomentum);
  }, [velocity, position, applyBoundaries]);

  // Start momentum animation when drag ends
  useEffect(() => {
    if (!isDragging && (Math.abs(velocity.x) > 0.5 || Math.abs(velocity.y) > 0.5)) {
      animationRef.current = requestAnimationFrame(animateWithMomentum);
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isDragging, velocity, animateWithMomentum]);

  useEffect(() => {
    if (isVideoPlay && isIOS()) {
      dispatch(onPauseVideo());
    }
  }, []);

  // Advanced drag functionality with momentum
  const handleDragStart = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      // Prevent window scroll immediately
      e.preventDefault();
      e.stopPropagation();

      // Show controls when mouse/touch down
      setShowControls(true);
      // Show close zone when dragging starts
      setShowCloseZone(true);

      // Cancel any ongoing momentum animation
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }

      const touches = "touches" in e ? e.touches : null;

      if (touches && touches.length === 2) {
        // Pinch gesture start
        setIsPinching(true);
        setIsDragging(false);
        initialDistance.current = getDistance(touches[0], touches[1]);
        initialScale.current = scale;
        return;
      }

      // Single touch drag
      setIsDragging(true);
      setIsPinching(false);

      const clientX = touches ? touches[0].clientX : (e as React.MouseEvent).clientX;
      const clientY = touches ? touches[0].clientY : (e as React.MouseEvent).clientY;

      initialMousePos.current = { x: clientX, y: clientY };
      dragStartPos.current = { ...position };
      lastPosition.current = { ...position };
      lastMoveTime.current = Date.now();
      setVelocity({ x: 0, y: 0 });
    },
    [position, scale],
  );

  const handleDragMove = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (e.cancelable) {
        e.preventDefault();
      }

      const touches = "touches" in e ? e.touches : null;

      if (isPinching && touches && touches.length === 2) {
        // Handle pinch-to-zoom (100% to 125% range)
        const currentDistance = getDistance(touches[0], touches[1]);
        const scaleChange = currentDistance / initialDistance.current;
        const newScale = Math.max(1.0, Math.min(1.25, initialScale.current * scaleChange));

        setScale(newScale);
        return;
      }

      if (!isDragging) return;

      const currentTime = Date.now();
      const clientX = touches ? touches[0].clientX : (e as MouseEvent).clientX;
      const clientY = touches ? touches[0].clientY : (e as MouseEvent).clientY;

      const deltaX = clientX - initialMousePos.current.x;
      const deltaY = clientY - initialMousePos.current.y;

      const newPos = applyBoundaries(dragStartPos.current.x + deltaX, dragStartPos.current.y + deltaY);

      // Check if more than 50% of popup is behind close zone (bottom 100px of screen)
      const playerHeight = 135 * scale;
      const playerTop = newPos.y;
      const playerBottom = newPos.y + playerHeight;
      const closeZoneTop = window.innerHeight - 100;

      // Calculate how much of the player is in the close zone
      const playerInCloseZone = Math.max(0, playerBottom - closeZoneTop);
      const percentageInCloseZone = playerInCloseZone / playerHeight;

      // Activate close when more than 50% is in close zone
      const isOverClose = percentageInCloseZone > 0.5;

      setIsOverCloseZone(isOverClose);

      // Calculate velocity for momentum
      const timeDelta = currentTime - lastMoveTime.current;
      if (timeDelta > 0) {
        const velocityX = ((newPos.x - lastPosition.current.x) / timeDelta) * 16; // 60fps normalization
        const velocityY = ((newPos.y - lastPosition.current.y) / timeDelta) * 16;

        setVelocity({ x: velocityX, y: velocityY });
      }

      setPosition(newPos);
      lastPosition.current = newPos;
      lastMoveTime.current = currentTime;
    },
    [isPinching, isDragging, applyBoundaries, scale],
  );

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    setIsPinching(false);

    // Hide close zone when drag ends
    setShowCloseZone(false);

    // If player was over close zone, close the popup
    if (isOverCloseZone) {
      onClose();
      return;
    }

    // Keep controls visible for 5 seconds after touch end
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }

    controlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 5000); // Hide after 5 seconds

    // Reset close zone state
    setIsOverCloseZone(false);

    // Momentum will be handled by the useEffect above
  }, [isOverCloseZone, onClose]);

  // Prevent body scroll during drag
  useEffect(() => {
    if (isDragging || isPinching) {
      // Prevent body scroll during drag
      const originalOverflow = document.body.style.overflow;
      const originalTouchAction = document.body.style.touchAction;

      document.body.style.overflow = "hidden";
      document.body.style.touchAction = "none";

      document.addEventListener("mousemove", handleDragMove);
      document.addEventListener("mouseup", handleDragEnd);
      document.addEventListener("touchmove", handleDragMove, { passive: false });
      document.addEventListener("touchend", handleDragEnd, { passive: false });

      return () => {
        // Restore body scroll
        document.body.style.overflow = originalOverflow;
        document.body.style.touchAction = originalTouchAction;

        document.removeEventListener("mousemove", handleDragMove);
        document.removeEventListener("mouseup", handleDragEnd);
        document.removeEventListener("touchmove", handleDragMove);
        document.removeEventListener("touchend", handleDragEnd);
      };
    }
  }, [isDragging, isPinching, handleDragMove, handleDragEnd]);

  // Controls visibility management
  const showControlsTemporarily = useCallback(() => {
    setShowControls(true);

    // Clear existing timeout
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }

    // Hide controls after 4 seconds
    controlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 4000);
  }, []);

  // Separate touch handling for showing controls vs dragging
  const touchStartTime = useRef(0);
  const touchStartPos = useRef({ x: 0, y: 0 });
  const hasMoved = useRef(false);

  const handleVideoTouchStart = useCallback((e: React.TouchEvent) => {
    // Don't handle if touching a button - check more thoroughly
    const target = e.target as HTMLElement;
    if (target.closest("button") || target.tagName === "BUTTON" || target.closest('[role="button"]')) {
      return;
    }

    touchStartTime.current = Date.now();
    touchStartPos.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
    hasMoved.current = false;
  }, []);

  const handleVideoTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      // Don't handle if touching a button
      if ((e.target as HTMLElement).closest("button")) {
        return;
      }

      const touchEndTime = Date.now();
      const touchDuration = touchEndTime - touchStartTime.current;

      // If it was a quick tap (less than 200ms) and didn't move much, show controls
      if (touchDuration < 200 && !hasMoved.current && !isDragging) {
        e.preventDefault();
        e.stopPropagation();
        showControlsTemporarily();
      }
    },
    [isDragging, showControlsTemporarily],
  );

  const handleVideoTouchMove = useCallback((e: React.TouchEvent) => {
    const currentPos = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };

    const deltaX = Math.abs(currentPos.x - touchStartPos.current.x);
    const deltaY = Math.abs(currentPos.y - touchStartPos.current.y);

    // If moved more than 10px, consider it a drag
    if (deltaX > 10 || deltaY > 10) {
      hasMoved.current = true;
    }
  }, []);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      {/* JioHotstar-like Bottom Close Zone - Only visible when actively dragging */}
      {isDragging && (
        <div className="fixed bottom-0 left-0 right-0 z-50 h-24 to-transparent flex items-end justify-center pb-4 transition-all duration-300">
          <div
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-full backdrop-blur-sm transition-all duration-200",
              isOverCloseZone ? "bg-textHeadingColor text-white scale-110" : "bg-secondaryColor !text-textSecondaryColor",
            )}
          >
            <MdOutlineClose className={cn(isOverCloseZone ? "text-white" : "text-textSecondaryColor", "text-lg")} />
            <span className={cn(isOverCloseZone ? "text-white" : "text-textSecondaryColor", "font-medium text-sm")}>
              {isOverCloseZone ? "Release to Close" : "Drag here to Close"}
            </span>
          </div>
        </div>
      )}

      {/* Mobile View - JioHotstar-like draggable player */}
      <div
        ref={dragRef}
        className="fixed z-40"
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
          width: `${240 * scale}px`,
          height: `${135 * scale}px`,
          transform: `scale(${isDragging ? scale * 1.05 : scale})`,
          transformOrigin: "center center",
          willChange: isDragging || isPinching ? "transform" : "auto",
          transition: isDragging || isPinching ? "none" : "transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
        }}
      >
        {/* Top Controls - Outside drag layer for better event handling */}
        {showControls && (
          <div className="absolute top-2 right-2 flex gap-2 z-50">
            {/* <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onHandleMuteUnMute();
              }}
              className="p-1.5 bg-black/50 rounded-full backdrop-blur-sm cursor-pointer hover:bg-black/70 transition-all duration-200"
              title={isMute ? "Unmute" : "Mute"}
            >
              {isMute ? <MdOutlineVolumeOff className="text-white text-sm" /> : <MdOutlineVolumeUp className="text-white text-sm" />}
            </button> */}
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onFullScreen();
              }}
              className="p-1.5 bg-black/50 rounded-full backdrop-blur-sm cursor-pointer hover:bg-black/70 transition-all duration-200"
              title="Go to full screen"
            >
              <AiOutlineFullscreen className="text-white text-sm" />
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClose();
              }}
              className="p-1.5 bg-black/50 rounded-full backdrop-blur-sm cursor-pointer hover:bg-black/70 transition-all duration-200"
              title="Close player"
            >
              <MdOutlineClose className="text-white text-sm" />
            </button>
          </div>
        )}

        {/* Main Player Container */}
        {/* Video Player */}
        <div className="relative bg-black rounded-xl overflow-hidden shadow-2xl border border-gray-800 h-full">
          {/* Video Player */}
          <div className="relative w-full h-full">
            <YoutubeVideoPlayer
              key={currentVideo?.videoId}
              handlePlayVideo={() => playCurrentVideo(currentVideo?.videoId || null)}
              videoId={currentVideo ? currentVideo.videoId : "PJab2ScnQQs"}
              handleNextVideo={onNextVideo}
            />

            {/* Combined Touch/Drag Layer */}
            <div
              className="absolute inset-0 cursor-move"
              onMouseDown={(e) => {
                // Don't handle if clicking on a button
                const target = e.target as HTMLElement;
                if (target.closest("button") || target.tagName === "BUTTON") {
                  return;
                }
                handleDragStart(e);
              }}
              onTouchStart={(e) => {
                // Don't handle if touching a button
                const target = e.target as HTMLElement;
                if (target.closest("button") || target.tagName === "BUTTON") {
                  return;
                }
                // Handle both touch for controls and drag start
                handleVideoTouchStart(e);
                handleDragStart(e);
              }}
              onTouchMove={handleVideoTouchMove}
              onTouchEnd={handleVideoTouchEnd}
            >
              {/* Controls Overlay - Only visible when showControls is true */}
              <div
                className={cn(
                  "absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/40 transition-opacity duration-300",
                  showControls ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none",
                )}
              >
                {/* Top Controls */}
                <div className="absolute top-2 right-2 flex gap-2 z-20">
                  {/* <button
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onTouchStart={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onHandleMuteUnMute();
                    }}
                    className="p-1.5 bg-black/50 rounded-full backdrop-blur-sm cursor-pointer hover:bg-black/70 transition-all duration-200 pointer-events-auto relative z-30"
                    title={isMute ? "Unmute" : "Mute"}
                  >
                    {isMute ? (
                      <MdOutlineVolumeOff className="text-white text-sm pointer-events-none" />
                    ) : (
                      <MdOutlineVolumeUp className="text-white text-sm pointer-events-none" />
                    )}
                  </button> */}
                  <button
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onTouchStart={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onFullScreen();
                    }}
                    className="p-1.5 bg-black/50 rounded-full backdrop-blur-sm cursor-pointer hover:bg-black/70 transition-all duration-200 pointer-events-auto relative z-30"
                    title="Go to full screen"
                  >
                    <AiOutlineFullscreen className="text-white text-sm pointer-events-none" />
                  </button>
                  <button
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onTouchStart={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onClose();
                    }}
                    className="p-1.5 bg-black/50 rounded-full backdrop-blur-sm cursor-pointer hover:bg-black/70 transition-all duration-200 pointer-events-auto relative z-30"
                    title="Close player"
                  >
                    <MdOutlineClose className="text-white text-sm pointer-events-none" />
                  </button>
                </div>

                {/* Center Controls - Play/Pause with Next/Previous */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-auto">
                  <div className="flex items-center gap-4">
                    {/* Play/Pause Button */}
                    <button
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onTouchStart={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={(e) => {
                        onPlayPause(e);
                      }}
                      className="p-3 bg-black/50 rounded-full backdrop-blur-sm cursor-pointer hover:bg-black/70 transition-all duration-200"
                      title={isVideoPlay ? "Pause" : "Play"}
                    >
                      {isVideoPlay ? <MdPause className="text-white text-xl" /> : <MdPlayArrow className="text-white text-xl" />}
                    </button>
                  </div>
                </div>
              </div>

              {/* Always visible video info at bottom (outside controls overlay) */}
              {currentPlayVideo?.name && (
                <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/40 to-transparent pointer-events-none">
                  <p className={cn("text-white text-xs line-clamp-1 opacity-80", medium)}>{currentPlayVideo.name}</p>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* <div className="relative  border border-gray-800 h-full"></div> */}
      </div>
    </>
  );
}

export default memo(MobileVideoPopup);
